import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const HERO_IMG =
  "https://images.unsplash.com/photo-1598981457915-aea220950616?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxzd2VkaXNoJTIwc3R1ZGVudCUyMHN0dWR5aW5nJTIwY2FsbSUyMG1pbmltYWxpc3QlMjBoaWdoJTIwc2Nob29sfGVufDB8fHx8MTc4ODg5MTA4OHww&ixlib=rb-4.1.0&q=85";

function GoogleButton({ label }) {
  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button
      type="button"
      variant="outline"
      onClick={handleGoogle}
      data-testid="google-login-button"
      className="w-full rounded-full border-[#1A1F1C]/15 bg-white text-[#1A1F1C] hover:bg-[#F4F4F0]"
    >
      <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
        <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
      </svg>
      {label}
    </Button>
  );
}

function AuthShell({ title, subtitle, children }) {
  const { t } = useLanguage();
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FBFBF9]">
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-16 py-12">
        <div className="w-full max-w-md mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[#5B655F] hover:text-[#1A1F1C] transition-colors mb-10"
            data-testid="auth-back-home"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("auth.backHome")}
          </Link>
          <div className="flex items-center gap-2.5 mb-8">
            <span className="h-7 w-7 rounded-lg bg-[#1E3A2B] flex items-center justify-center">
              <span className="h-2.5 w-2.5 rounded-full bg-[#C27B2B]" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading font-bold text-sm text-[#1A1F1C]">Reach New Edge™</span>
              <span className="font-mono-label text-[9px] text-[#5B655F] mt-0.5">Powered by Learning Recovery OS™</span>
            </span>
          </div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-[#1A1F1C]">{title}</h1>
          <p className="mt-2 text-base text-[#525B56]">{subtitle}</p>
          <div className="mt-8">{children}</div>
        </div>
      </div>
      <div className="hidden lg:block relative">
        <img src={HERO_IMG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#1E3A2B]/30" />
      </div>
    </div>
  );
}

export function Login() {
  const { t } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title={t("auth.loginTitle")} subtitle={t("auth.loginSubtitle")}>
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="email">{t("auth.email")}</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" className="rounded-xl bg-white" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">{t("auth.password")}</Label>
          <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" className="rounded-xl bg-white" />
        </div>
        {error && (
          <p className="text-sm text-[#B91C1C]" role="alert" data-testid="login-error">
            {error}
          </p>
        )}
        <Button type="submit" disabled={busy} data-testid="login-submit-button" className="w-full rounded-full bg-[#1E3A2B] hover:bg-[#14281E] text-[#FBFBF9]">
          {busy ? t("common.loading") : t("auth.loginBtn")}
        </Button>
      </form>
      <div className="my-6 flex items-center gap-4">
        <span className="h-px flex-1 bg-[#1A1F1C]/10" />
        <span className="font-mono-label text-[10px] text-[#5B655F]">{t("auth.or")}</span>
        <span className="h-px flex-1 bg-[#1A1F1C]/10" />
      </div>
      <GoogleButton label={t("auth.google")} />
      <p className="mt-8 text-sm text-[#525B56] text-center">
        {t("auth.noAccount")}{" "}
        <Link to="/register" className="text-[#1E3A2B] font-medium underline-offset-2 hover:underline" data-testid="go-register-link">
          {t("auth.goRegister")}
        </Link>
      </p>
    </AuthShell>
  );
}

export function Register() {
  const { t } = useLanguage();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await register(form);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title={t("auth.registerTitle")} subtitle={t("auth.registerSubtitle")}>
      <form onSubmit={submit} className="space-y-5" data-testid="register-form">
        <div className="space-y-2">
          <Label htmlFor="name">{t("auth.name")}</Label>
          <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="register-name-input" className="rounded-xl bg-white" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="remail">{t("auth.email")}</Label>
          <Input id="remail" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email-input" className="rounded-xl bg-white" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="rpassword">{t("auth.password")}</Label>
          <Input id="rpassword" type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="register-password-input" className="rounded-xl bg-white" />
        </div>
        <div className="space-y-2">
          <Label>{t("auth.role")}</Label>
          <p className="rounded-xl border border-[#1A1F1C]/12 bg-[#F4F4F0] px-3 py-2.5 text-sm text-[#525B56]" data-testid="register-role-note">
            {t("auth.selfRegisterNote")}
          </p>
        </div>
        {error && (
          <p className="text-sm text-[#B91C1C]" role="alert" data-testid="register-error">
            {error}
          </p>
        )}
        <Button type="submit" disabled={busy} data-testid="register-submit-button" className="w-full rounded-full bg-[#1E3A2B] hover:bg-[#14281E] text-[#FBFBF9]">
          {busy ? t("common.loading") : t("auth.registerBtn")}
        </Button>
      </form>
      <div className="my-6 flex items-center gap-4">
        <span className="h-px flex-1 bg-[#1A1F1C]/10" />
        <span className="font-mono-label text-[10px] text-[#5B655F]">{t("auth.or")}</span>
        <span className="h-px flex-1 bg-[#1A1F1C]/10" />
      </div>
      <GoogleButton label={t("auth.google")} />
      <p className="mt-8 text-sm text-[#525B56] text-center">
        {t("auth.hasAccount")}{" "}
        <Link to="/login" className="text-[#1E3A2B] font-medium underline-offset-2 hover:underline" data-testid="go-login-link">
          {t("auth.goLogin")}
        </Link>
      </p>
    </AuthShell>
  );
}
